import { useState, useEffect } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { getBankUsageDaily } from "@/lib/api";
import { logWarn } from "@/lib/log";
import { Loader2, TrendingDown } from "lucide-react";

const AVG_WINDOW_DAYS = 7;
const CHART_MARGIN = { top: 5, right: 5, left: -20, bottom: 0 };
const TICK_STYLE = { fill: "rgba(255,255,255,0.35)", fontSize: 10, fontFamily: "monospace" };
const X_AXIS_LINE = { stroke: "rgba(255,255,255,0.1)" };
const CURSOR_STYLE = { fill: "rgba(255,255,255,0.05)" };
const BAR_RADIUS = [2, 2, 0, 0];

const shortDay = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
};

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="border border-white/20 bg-obsidian px-3 py-2 font-mono text-[11px]">
            <div className="text-white/50">{label}</div>
            <div className="text-blaze font-bold">{payload[0].value.toLocaleString()} credits</div>
        </div>
    );
};

const BurnForecast = ({ days, balance }) => {
    const recent = days.slice(-AVG_WINDOW_DAYS);
    const avg = recent.reduce((s, d) => s + d.credits, 0) / recent.length;
    if (avg <= 0) {
        return (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30" data-testid="usage-days-left">
                No burn yet — forecast appears once users stream
            </span>
        );
    }
    const daysLeft = Math.floor(Math.max(balance, 0) / avg);
    return (
        <span
            className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] ${
                daysLeft <= 3 ? "text-blaze" : "text-white/50"
            }`}
            data-testid="usage-days-left"
        >
            <TrendingDown className="w-3.5 h-3.5" />
            Avg {Math.round(avg).toLocaleString()}/day · bank lasts ≈ {daysLeft} day{daysLeft === 1 ? "" : "s"}
        </span>
    );
};

export const UsageChart = ({ bankBalance }) => {
    const [days, setDays] = useState(null);

    useEffect(() => {
        getBankUsageDaily(14)
            .then((d) => setDays(d.days))
            .catch((e) => logWarn("usage chart load failed", e));
    }, []);

    return (
        <div className="border border-white/10 bg-surface p-6 mb-6" data-testid="usage-chart-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                    Credits Burned Per Day — Last 14 Days
                </div>
                {days && <BurnForecast days={days} balance={bankBalance} />}
            </div>
            {days === null ? (
                <div className="h-48 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-blaze animate-spin" />
                </div>
            ) : (
                <div className="h-48" data-testid="usage-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={days.map((d) => ({ ...d, label: shortDay(d.date) }))} margin={CHART_MARGIN}>
                            <XAxis dataKey="label" tick={TICK_STYLE} axisLine={X_AXIS_LINE} tickLine={false} />
                            <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip content={<ChartTooltip />} cursor={CURSOR_STYLE} />
                            <Bar dataKey="credits" fill="#FF4500" radius={BAR_RADIUS} maxBarSize={40} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};
